'use client'

import { FIXTURES } from './fixtures-flag'

// Meta Pixel contract. MARKETING SURFACE ONLY — see MetaPixel.tsx for why that
// boundary is load-bearing rather than a preference.
//
// HOW THIS DIFFERS FROM THE GA4 TAG NEXT DOOR
//
// lib/gtag.ts can sanitise what it reports, because it builds `page_location`
// itself. The Pixel cannot: it reads the page URL at send time and exposes no
// supported client-side override — which is why Meta ships URL blocklists in
// Events Manager rather than a code-side setting. So the only control we have
// over what Meta receives is WHERE the tag runs at all. It runs on the two
// public routes whose URLs carry nothing but campaign parameters:
//
//   /         the logged-out landing page
//   /thanks   the demo-request confirmation
//
// Not the app (authed URLs carry ?client_id= / ?clientId= / ?view=, which are
// tenant identifiers), and not the auth screens (/sign-up carries
// __clerk_ticket, an invite token). Both were verified against the routes, not
// assumed.
//
// Nothing is lost by that boundary: every ad-attributable conversion happens
// BEFORE login. A signed-in user is already a customer.

/** Pixel id, inlined at build time. Absent in dev/test/staging. */
export const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FB_PIXEL_ID ?? ''

/** FIXTURES is QA traffic, not signal — same exclusion the other tags make. */
export const FB_PIXEL_ENABLED = FB_PIXEL_ID !== '' && !FIXTURES

/**
 * The canonical Meta bootstrap, inlined (ENG-4154).
 *
 * WHY THIS IS A STRING AND NOT A `next/script` src.
 *
 * `fbevents.js` does NOT create `window.fbq`. It expects to find an `fbq`
 * already present — a queue stub — and attaches its real implementation to it.
 * Loaded into a vacuum it defines nothing, silently: the script still returns
 * 200, no console error is raised, and every later `fbq(...)` call is a no-op
 * against `undefined`.
 *
 * The first revision of this file omitted the stub on the reasoning that
 * `next/script`'s `onReady` "fires after window.fbq exists". It does not exist
 * — the stub is what brings it into being. The pixel shipped, looked installed,
 * and recorded nothing at all.
 *
 * So the stub and the loader stay welded together in one inline script, exactly
 * as Meta ships it: the stub defines `fbq`, THEN injects the library. That
 * ordering is the whole fix and it cannot be got wrong by a scheduler, because
 * nothing else schedules it.
 *
 * `autoConfig` is set BEFORE `init` (Meta ignores it after), and the entry
 * PageView is fired here rather than from an effect so the ad landing itself is
 * counted even if hydration is slow.
 */
export function fbPixelSnippet(): string {
  return `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('set','autoConfig',false,'${FB_PIXEL_ID}');
fbq('init','${FB_PIXEL_ID}');
fbq('track','PageView');`
}

/** The `<noscript>` fallback beacon, same id. */
export const FB_PIXEL_NOSCRIPT_SRC = `https://www.facebook.com/tr?id=${FB_PIXEL_ID}&ev=PageView&noscript=1`

/**
 * Standard events Meta's delivery model understands. A closed union rather than
 * a free string: a custom name still records, but Meta can only OPTIMISE ad
 * delivery toward its standard events, so a typo silently downgrades a campaign
 * from "find people who convert" to "find people who click".
 */
export type FbStandardEvent = 'PageView' | 'Lead' | 'CompleteRegistration' | 'Contact'

type FbqArgs =
  | ['init', string]
  | ['set', 'autoConfig', boolean, string]
  | ['track', FbStandardEvent, Record<string, unknown>?]

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void }
    _fbq?: unknown
  }
}

function fbq(...args: FbqArgs): void {
  window.fbq?.(...args)
}

/**
 * PageView for a route.
 *
 * Sent by hand on every navigation because the canonical snippet fires once and
 * never again — the app router navigates with the history API, so on a SPA the
 * snippet's single PageView is the only one Meta would ever see.
 */
export function fbPageView(): void {
  if (!FB_PIXEL_ENABLED || typeof window === 'undefined') return
  fbq('track', 'PageView')
}

/**
 * A standard conversion event.
 *
 * This is what makes the Pixel worth having: it lets Meta optimise delivery
 * toward people who convert rather than people who click. Parameters must stay
 * non-PII — Meta's Business Tools Terms forbid sending financial account
 * information, and enforcement lands on us, not them.
 */
export function fbEvent(name: FbStandardEvent, params?: Record<string, unknown>): void {
  if (!FB_PIXEL_ENABLED || typeof window === 'undefined') return
  fbq('track', name, params)
}
